'use client'
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import Image from 'next/image'
import Link from 'next/link'
import { Offer, ProductData } from '@/services/dataTypes'
import { discardHTMLTags, getBaseImageUrl, getCurrencySymbol, calculateOfferDuration } from '@/constants/hooks'
import { X, Copy, Check, ExternalLink, Star, Clock, Infinity, AlertTriangle, Tag, ArrowRight, Leaf, ChevronDown, FileText } from 'lucide-react'
import { apiAddComment } from '@/apis/offers'
import { toast } from 'react-toastify'

interface Props {
    data: Offer | ProductData
    onClose: () => void
    domain: string
    merchantHref: string
    finalDiscountTag?: string | null
    merchantImg?: string | null
    productImg?: string | null
}

const SimpleOfferModal = ({ data, onClose, domain, merchantHref, finalDiscountTag, merchantImg, productImg }: Props) => {
    const [mounted, setMounted] = useState(false)
    const [copied, setCopied] = useState(false)
    const [revealed, setRevealed] = useState(false)
    const [rating, setRating] = useState(0)
    const [hover, setHover] = useState(0)
    const [comment, setComment] = useState('')
    const [feedbackSent, setFeedbackSent] = useState(false)
    const [hasRated, setHasRated] = useState(false)
    const [feedbackLoading, setFeedbackLoading] = useState(false)
    const [tab, setTab] = useState<'deal' | 'review'>('deal')
    const [isExpanded, setIsExpanded] = useState(false)

    const imageSrc = productImg
        ? getBaseImageUrl(domain, productImg, '')
        : getBaseImageUrl(domain, merchantImg ?? '', '')

    useEffect(() => {
        setMounted(true)
        document.body.style.overflow = 'hidden'
        const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', handleEsc)
        if (data?.unique_id) {
            const lastRated = localStorage.getItem(`hasRated_${data.unique_id}`)
            if (lastRated && Date.now() - parseInt(lastRated) < 86400000) {
                setHasRated(true); setFeedbackSent(true)
            }
        }
        return () => { window.removeEventListener('keydown', handleEsc); document.body.style.overflow = '' }
    }, [onClose, data?.unique_id])

    const handleCopy = async () => {
        if (!data?.coupon_code) return
        try {
            await navigator.clipboard.writeText(data.coupon_code)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            try {
                const el = document.createElement('textarea')
                el.value = data.coupon_code
                el.style.position = 'fixed'
                el.style.opacity = '0'
                const container = document.body || document.documentElement
                if (container) {
                    container.appendChild(el)
                    el.focus()
                    el.select()
                    document.execCommand('copy')
                    container.removeChild(el)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                }
            } catch {
                toast.error('Failed to copy. Please copy manually.')
            }
        }
    }

    const handleFeedback = async () => {
        if (hasRated) { toast.error('Already rated, try again later!'); return }
        if (!rating && !comment.trim()) { toast.error('Please select a rating or add a comment.'); return }
        setFeedbackLoading(true)
        try {
            const res = await apiAddComment(data?.unique_id, '', comment, rating.toString())
            if (res.status === 'success') {
                toast.success('Thank you for your feedback!')
                localStorage.setItem(`hasRated_${data.unique_id}`, Date.now().toString())
                setHasRated(true); setFeedbackSent(true)
                setComment(''); setRating(0)
            } else toast.error('Something went wrong.')
        } catch { toast.error('Something went wrong.') }
        finally { setFeedbackLoading(false) }
    }

    const duration = calculateOfferDuration(data?.end_date ?? null)
    const originalPrice = data?.original_price ? parseFloat(data.original_price) : 0
    const salePrice = data?.sale_price ? parseFloat(data.sale_price) : 0
    const hasDiscount = originalPrice > 0 && salePrice > 0 && originalPrice > salePrice
    const currency = getCurrencySymbol(data?.currency)
    const isCoupon = !!data?.coupon_code
    const finalUrl = data?.url?.startsWith('/') ? data.url.replace(/^\/+/, '') : data?.url

    if (!mounted) return null

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-3.5 sm:p-4 overflow-y-auto"
            style={{ background: 'rgba(0,0,0,0.55)', transform: 'translateZ(0)', willChange: 'transform', isolation: 'isolate', pointerEvents: 'all' }}
            onClick={onClose}>

            {/* Centered card modal */}
            <div className="w-full sm:max-w-lg relative flex flex-col rounded-3xl overflow-hidden"
                style={{
                    background: '#FAF7F2',
                    maxHeight: '88vh',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
                }}
                onClick={e => e.stopPropagation()}>

                {/* Close button */}
                <button onClick={onClose}
                    className="absolute top-3.5 right-3.5 z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
                    style={{ background: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' }}>
                    <X size={15} />
                </button>

                {/* ── TOP HERO BAND ── */}
                <div className="flex items-center gap-4 px-5 py-4 flex-shrink-0"
                    style={{ background: '#2D3E2D' }}>
                    {imageSrc && (
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center p-2 flex-shrink-0"
                            style={{ background: 'rgba(250,247,242,0.1)' }}>
                            <Image src={imageSrc} alt={data?.offer_title || ''} width={52} height={52} className="object-contain" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0 pr-8 sm:pr-0">
                        <div className="flex flex-wrap gap-1.5 mb-1.5">
                            {finalDiscountTag && (
                                <span className="text-[9px] font-black px-2 py-0.5 rounded-full"
                                    style={{ background: '#F5C542', color: '#2D3E2D' }}>
                                    {finalDiscountTag}
                                </span>
                            )}
                            <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-2 py-0.5 rounded-full"
                                style={{ background: 'rgba(232,240,232,0.15)', color: 'rgba(232,240,232,0.8)' }}>
                                {duration === 'Life Time' ? <Infinity size={8} /> :
                                    duration === 'Offer expired' ? <AlertTriangle size={8} /> :
                                        <Clock size={8} />}
                                {duration}
                            </span>
                        </div>
                        <h2 className="text-sm font-black leading-snug line-clamp-2"
                            style={{ color: '#FAF7F2', fontFamily: 'Georgia, serif' }}>
                            {discardHTMLTags(data?.offer_title)}
                        </h2>
                    </div>
                </div>

                {/* ── TABS ── */}
                <div className="flex flex-shrink-0" style={{ borderBottom: '1px solid rgba(92,122,92,0.12)' }}>
                    {(['deal', 'review'] as const).map(t => (
                        <button key={t} onClick={() => setTab(t)}
                            className="flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-all"
                            style={{
                                color: tab === t ? '#2D3E2D' : 'rgba(45,62,45,0.4)',
                                borderBottom: tab === t ? '2px solid #F5C542' : '2px solid transparent',
                                background: 'transparent',
                            }}>
                            {t === 'deal' ? 'Deal Info' : 'Leave Review'}
                        </button>
                    ))}
                </div>

                {/* ── SCROLLABLE BODY ── */}
                <div className="flex-1 overflow-y-auto">

                    {/* DEAL TAB */}
                    {tab === 'deal' && (
                        <div className="px-5 py-5 space-y-4">

                            {/* Price block */}
                            {salePrice > 0 && (
                                <div className="flex items-center gap-3 px-4 py-3 rounded-2xl"
                                    style={{ background: '#FFFFFF', border: '1px solid rgba(92,122,92,0.12)' }}>
                                    <div>
                                        <p className="text-[9px] font-bold uppercase tracking-wider mb-0.5"
                                            style={{ color: 'rgba(45,62,45,0.4)' }}>Price</p>
                                        <span className="text-2xl font-black" style={{ color: '#2D3E2D', fontFamily: 'Georgia, serif' }}>
                                            {currency}{salePrice.toFixed(2)}
                                        </span>
                                    </div>
                                    {hasDiscount && (
                                        <div className="ml-auto text-right">
                                            <span className="text-sm line-through block" style={{ color: 'rgba(45,62,45,0.35)' }}>
                                                {currency}{originalPrice.toFixed(2)}
                                            </span>
                                            <span className="text-xs font-black px-2 py-0.5 rounded-full text-white"
                                                style={{ background: '#5C7A5C' }}>
                                                -{Math.round(((originalPrice - salePrice) / originalPrice) * 100)}%
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Coupon code */}
                            {isCoupon && (
                                <div className="rounded-2xl overflow-hidden"
                                    style={{ border: '1px solid rgba(92,122,92,0.15)' }}>
                                    <div className="px-4 py-2 flex items-center gap-1.5"
                                        style={{ background: '#E8F0E8', borderBottom: '1px solid rgba(92,122,92,0.12)' }}>
                                        <Tag size={11} style={{ color: '#5C7A5C' }} />
                                        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#5C7A5C' }}>
                                            Coupon Code
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3 px-4 py-3"
                                        style={{ background: '#FFFFFF' }}>
                                        <code className="flex-1 text-base font-mono font-black select-all"
                                            style={{ color: '#2D3E2D', letterSpacing: '0.1em' }}>
                                            {data.coupon_code?.trim()}
                                        </code>
                                        <button onClick={handleCopy}
                                            className="px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:-translate-y-0.5 flex items-center gap-1.5 flex-shrink-0 cursor-pointer"
                                            style={{ background: '#2D3E2D' }}>
                                            {copied ? <><Check size={11} /> Copied!</> : <><Copy size={11} /> Copy Code</>}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Description */}
                            {data?.offer_detail && (() => {
                                const cleanDetail = discardHTMLTags(data.offer_detail)
                                const isLong = cleanDetail.length > 120
                                return (
                                    <div className="rounded-2xl px-4 py-3"
                                        style={{ background: '#FFFFFF', border: '1px solid rgba(92,122,92,0.12)' }}>
                                        <p className="text-[9px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5"
                                            style={{ color: '#2D3E2D' }}>
                                            <FileText size={11} className="text-[#5C7A5C]" />
                                            About this offer
                                        </p>
                                        <p className={`text-xs leading-relaxed ${isLong && !isExpanded ? 'line-clamp-3' : ''}`} style={{ color: 'rgba(45,62,45,0.75)' }}>
                                            {cleanDetail}
                                        </p>
                                        {isLong && (
                                            <button
                                                type="button"
                                                onClick={() => setIsExpanded(!isExpanded)}
                                                className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-[#5C7A5C] hover:underline cursor-pointer"
                                            >
                                                <span>{isExpanded ? 'Read Less' : 'Read More'}</span>
                                                <ChevronDown size={13} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                            </button>
                                        )}
                                    </div>
                                )
                            })()}

                            {/* Trust row */}
                            <div className="flex items-center gap-3 flex-wrap">
                                {[
                                    { icon: Leaf, label: '100% Verified' },
                                    { icon: Clock, label: duration },
                                ].map((b, i) => (
                                    <div key={i} className="flex items-center gap-1.5">
                                        <b.icon size={11} style={{ color: '#5C7A5C' }} />
                                        <span className="text-[10px] font-semibold" style={{ color: 'rgba(45,62,45,0.5)' }}>{b.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* REVIEW TAB */}
                    {tab === 'review' && (
                        <div className="px-5 py-5">
                            {feedbackSent ? (
                                <div className="text-center py-10">
                                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                                        style={{ background: '#E8F0E8' }}>
                                        <Check size={24} style={{ color: '#5C7A5C' }} />
                                    </div>
                                    <p className="text-sm font-black" style={{ color: '#2D3E2D' }}>Thanks for your feedback!</p>
                                    <p className="text-xs mt-1" style={{ color: 'rgba(45,62,45,0.45)' }}>Your review helps other shoppers.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-xs font-bold mb-3" style={{ color: '#2D3E2D' }}>How would you rate this deal?</p>
                                        <div className="flex items-center gap-2"
                                            onMouseLeave={() => !feedbackLoading && setHover(0)}>
                                            {[1, 2, 3, 4, 5].map(star => (
                                                <button key={star} type="button"
                                                    onClick={() => !feedbackLoading && setRating(star)}
                                                    onMouseEnter={() => !feedbackLoading && setHover(star)}
                                                    disabled={feedbackLoading}
                                                    className="transition-transform hover:scale-110 disabled:opacity-50">
                                                    <Star size={28}
                                                        className={star <= (hover || rating)
                                                            ? 'fill-[#F5C542] text-[#F5C542]'
                                                            : 'fill-[rgba(92,122,92,0.15)] text-[rgba(92,122,92,0.15)]'}
                                                    />
                                                </button>
                                            ))}
                                            {rating > 0 && (
                                                <span className="text-xs font-bold ml-2" style={{ color: '#F5C542' }}>
                                                    {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold mb-2" style={{ color: '#2D3E2D' }}>Share your experience</p>
                                        <textarea
                                            value={comment}
                                            onChange={e => setComment(e.target.value)}
                                            placeholder="Did the coupon work? Any tips for other shoppers?"
                                            rows={3}
                                            disabled={feedbackLoading}
                                            className="w-full px-4 py-3 text-xs rounded-2xl resize-none focus:outline-none transition-all disabled:opacity-50"
                                            style={{
                                                background: '#FFFFFF',
                                                border: '1px solid rgba(92,122,92,0.2)',
                                                color: '#2D3E2D'
                                            }}
                                        />
                                    </div>
                                    <button onClick={handleFeedback}
                                        disabled={feedbackLoading || (!rating && !comment.trim())}
                                        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                        style={{ background: '#2D3E2D' }}>
                                        {feedbackLoading
                                            ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            : 'Submit Review'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── STICKY FOOTER CTA ── */}
                <div className="px-5 py-4 flex-shrink-0 flex items-center gap-3"
                    style={{ borderTop: '1px solid rgba(92,122,92,0.1)', background: '#FAF7F2' }}>
                    <Link href={merchantHref}
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all hover:-translate-y-0.5"
                        style={{ background: '#E8F0E8', border: '1px solid rgba(92,122,92,0.2)' }}>
                        <ExternalLink size={14} style={{ color: '#5C7A5C' }} />
                    </Link>
                    <Link href={`/${finalUrl}`} target="_blank" rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold text-white transition-all hover:-translate-y-0.5"
                        style={{ background: '#F5C542' }}>
                        {isCoupon ? 'Use Coupon' : 'Get This Deal'} <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default SimpleOfferModal
